import React from 'react';
import { Box, Chip, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { AccessTime as ClockIcon } from '@mui/icons-material';
import {
  PresenceWindow,
  defaultWindow,
  eventHoursOf,
  formatHours,
  hhmm,
  hoursBetween,
  quickOptions,
  snapToHalfHour,
  validateWindow,
} from '../utils/coachPresence';

interface Props {
  eventStart: string; // 'HH:mm:ss'
  eventEnd: string;   // 'HH:mm:ss'
  /** Null/undefined = végig jelen volt. */
  value?: PresenceWindow | null;
  onChange: (window: PresenceWindow | null) => void;
  disabled?: boolean;
  /** Telefonos kártya-nézet: a vezérlők egymás alá kerülnek. */
  compact?: boolean;
  testIdPrefix?: string;
}

/**
 * Az edző sorának „Végig / Részben" vezérlője a jelenléti íven: gyors-chipek az esemény hosszából
 * és kézi Kezdés–Befejezés mezők, félórás lépésben. Csak edző-sorra kerül; a játékosokra nincs hatással.
 */
const CoachPresenceControl: React.FC<Props> = ({ eventStart, eventEnd, value, onChange, disabled, compact, testIdPrefix = 'coach-presence' }) => {
  const isPartial = !!value;
  const options = quickOptions(eventStart, eventEnd);
  const totalHours = eventHoursOf(eventStart, eventEnd);
  const error = value ? validateWindow(eventStart, eventEnd, value) : null;
  const hours = value && !error ? hoursBetween(value.presentFrom, value.presentUntil) : null;

  const handleMode = (_: unknown, mode: 'full' | 'partial' | null) => {
    if (!mode || disabled) return;
    onChange(mode === 'partial' ? defaultWindow(eventStart, eventEnd) : null);
  };

  const handleTime = (field: keyof PresenceWindow, raw: string) => {
    if (!value || !raw) return;
    onChange({ ...value, [field]: snapToHalfHour(raw) });
  };

  const isSelected = (o: { presentFrom: string; presentUntil: string }) =>
    !!value && value.presentFrom === o.presentFrom && value.presentUntil === o.presentUntil;

  return (
    <Box
      data-testid={testIdPrefix}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.25,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isPartial ? 'warning.main' : 'divider',
        bgcolor: 'background.paper',
        width: compact ? '100%' : 'fit-content',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: compact ? 'space-between' : 'flex-start' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ClockIcon sx={{ fontSize: 16 }} /> Edzői jelenlét
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={isPartial ? 'partial' : 'full'}
          onChange={handleMode}
          disabled={disabled}
          data-testid={`${testIdPrefix}-mode`}
        >
          <ToggleButton value="full" data-testid={`${testIdPrefix}-full`} sx={{ px: 1.5, py: 0.5, textTransform: 'none' }}>Végig</ToggleButton>
          <ToggleButton value="partial" data-testid={`${testIdPrefix}-partial`} color="warning" sx={{ px: 1.5, py: 0.5, textTransform: 'none' }}>Részben</ToggleButton>
        </ToggleButtonGroup>
        {!compact && !isPartial && (
          <Typography variant="caption" color="text.secondary">{formatHours(totalHours)} óra</Typography>
        )}
      </Box>

      {isPartial && value && (
        <>
          {options.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {options.map(o => (
                <Chip
                  key={o.label}
                  label={o.label}
                  size="small"
                  clickable={!disabled}
                  disabled={disabled}
                  color={isSelected(o) ? 'warning' : 'default'}
                  variant={isSelected(o) ? 'filled' : 'outlined'}
                  onClick={() => !disabled && onChange({ presentFrom: o.presentFrom, presentUntil: o.presentUntil })}
                  data-testid={`${testIdPrefix}-quick-${o.label}`}
                />
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Kezdés"
              type="time"
              size="small"
              value={hhmm(value.presentFrom)}
              onChange={e => handleTime('presentFrom', e.target.value)}
              disabled={disabled}
              inputProps={{ step: 1800, min: hhmm(eventStart), max: hhmm(eventEnd), 'data-testid': `${testIdPrefix}-from` }}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: compact ? 1 : 'none', width: compact ? undefined : 120 }}
            />
            <Typography color="text.secondary">–</Typography>
            <TextField
              label="Befejezés"
              type="time"
              size="small"
              value={hhmm(value.presentUntil)}
              onChange={e => handleTime('presentUntil', e.target.value)}
              disabled={disabled}
              inputProps={{ step: 1800, min: hhmm(eventStart), max: hhmm(eventEnd), 'data-testid': `${testIdPrefix}-until` }}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: compact ? 1 : 'none', width: compact ? undefined : 120 }}
            />
          </Box>
          {error ? (
            <Typography variant="caption" color="error" data-testid={`${testIdPrefix}-error`}>{error}</Typography>
          ) : (
            <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }} data-testid={`${testIdPrefix}-summary`}>
              {formatHours(hours ?? 0)} / {formatHours(totalHours)} óra · a kifizetés {formatHours(hours ?? 0)} órára számol
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">félórás lépések (:00 / :30) · a játékosokat nem érinti</Typography>
        </>
      )}
    </Box>
  );
};

export default CoachPresenceControl;
